import { EventEmitter, Injectable, inject } from '@angular/core';
import {
  IAuth,
  IEducation,
  IMaterial,
  IUser,
  IWorkExperience,
  UserRole
} from '../../../../assets/types/dtoTypes';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { DataService } from 'src/app/shared/services/data.service';
import { catchError, switchMap } from 'rxjs/operators';

export interface IConsentPayload {
  type: 'PRIVACY' | 'TERMS' | 'DATA_PRIVACY_CONSENT' | 'COOKIE';
  version: string;
  accepted: boolean;
}

export interface IRegisterRequest {
  email: string;
  password: string;
  consents: IConsentPayload[];
}

export interface IEmailUsageResponse {
  used: boolean;
  // Whether the existing account is verified. An unverified email (used && !verified) must not
  // block registration — the user never finished verification and should be allowed to continue.
  verified?: boolean;
}

export interface ISocialConsentRequest {
  locale: string;
  source: string;
  consents: IConsentPayload[];
}

@Injectable({
  providedIn: 'root',
})
export class RegistrationPageService {
  private readonly dataService = inject(DataService);
  private readonly http = inject(HttpClient);

  openNextForm = new EventEmitter<string>();
  role = new EventEmitter<UserRole>();
  email = new EventEmitter<{ email: string, firstname: string, familyname: string }>();
  password = new EventEmitter<string>();
  consent = new EventEmitter<IConsentPayload[]>();
  bio = new EventEmitter<string>();

  parsedResume = new EventEmitter<{
    motivation: string,
    cvUrl: string,
    education: IEducation[],
    languages: string[],
    workExperience: IWorkExperience[],
  }>();

  personalInformation = new EventEmitter<{
    country: string,
    institutionCode: string,
    lookingFor: string,
    domain: string
  }>();

  certificates = new EventEmitter<{ certificationName: string, certificationYear: string }[]>();
  code = new EventEmitter<string>();

  private readonly selectedRole: UserRole;
  private readonly url = `${environment.apiUrl}/auth`;
  private readonly verificationUrl = `${environment.apiUrl}/auth/verify`;
  private readonly usersUrl = `${environment.apiUrl}/users`;
  private readonly resendUrl = `${environment.apiUrl}/auth/resend-verification`;
  private readonly parserUrl = `${environment.apiUrl}/cv/parse`;
  private readonly googleRegisterUrl = `${environment.apiUrl}/auth/google/register`;
  private readonly materialsUrl = `${environment.apiUrl}/materials`;
  private readonly emailUsedUrl = `${this.url}/email-used`;
  private readonly legalConsentsUrl = `${environment.apiUrl}/legal/consents`;

  public registerUser(payload: IRegisterRequest): Observable<IUser> {
    const registerUrl = `${this.url}/register`;
    return this.http.post<IUser>(registerUrl, payload);
  }

  public checkEmailUsage(email: string): Observable<IEmailUsageResponse> {
    return this.http.get<IEmailUsageResponse>(this.emailUsedUrl, { params: { email } });
  }

  public submitSocialSignupConsents(payload: ISocialConsentRequest): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(this.legalConsentsUrl, payload);
  }

  public registerUserWithAgency(user: Partial<IUser>): Observable<{ userId: string }> {
    const registerUrl = `${this.url}/agency-create-candidate`;
    return this.http.post<{ userId: string }>(registerUrl, user);
  }

  public registerWithGoogle(): void {
    const role = this.selectedRole;
    localStorage.setItem('isGoogleRegistration', 'true');
    localStorage.setItem('role', role);
    const url = `${environment.apiUrl}/coaches`;
    window.location.href = `${this.googleRegisterUrl}?role=${role}`;
    this.dataService.invalidateCache(url);
  }

  openForm(formName: string): void {
    this.openNextForm.emit(formName);
  }

  sendEmailAndUsername(email: string, firstname: string, lastname: string): void {
    this.email.emit({ email, firstname, familyname: lastname });
  }

  sendPassword(password: string): void {
    this.password.emit(password);
  }

  sendConsents(consents: IConsentPayload[]): void {
    this.consent.emit(consents);
  }

  sendCode(code: string): void {
    this.code.emit(code);
  }

  sendBio(bio: string): void {
    this.bio.emit(bio);
  }

  sendParsedResume(parsedResume: {
    education: IEducation[],
    motivation: string,
    languages: string[],
    cvUrl: string,
    workExperience: IWorkExperience[],
  }): void {
    this.parsedResume.emit(parsedResume);
  }

  sendPersonalInfo(personalInfo: {
    country: string,
    institutionCode: string,
    lookingFor: string,
    domain: string
  }): void {
    this.personalInformation.emit(personalInfo);
  }

  verifyUser(email: string, code: string, role: UserRole) {
    return this.http.post<IAuth>(this.verificationUrl, { code, email, role });
  }

  resendEmail(email: string) {
    return this.http.post<IAuth>(this.resendUrl, { email });
  }

  parseCV(url: string) {
    return this.http.post<Partial<IUser>>(this.parserUrl, { url }, { observe: 'response' }).pipe(
      switchMap((response) => of(response.body as Partial<IUser>)),
      catchError((error) => {
        if (error.status === 302) {
          return this.http.post<Partial<IUser>>(this.parserUrl, { url }, { observe: 'response' }).pipe(
            switchMap((response) => of(response.body as Partial<IUser>)),
            catchError((finalError) => throwError(() => finalError)),
          );
        }

        return throwError(() => error);
      }),
    );
  }

  public createNewMaterial(material: Partial<IMaterial>): Observable<IMaterial> {
    return this.http.post<IMaterial>(this.materialsUrl, material);
  }

  public updateUser(id: string, user: Partial<IUser>): Observable<Partial<IUser>> {
    const url = `${this.usersUrl}/${id}`;
    return this.http.put<Partial<IUser>>(url, user);
  }

  public getUser(id: string): Observable<IUser> {
    const url = `${this.usersUrl}/${id}`;
    return this.http.get<IUser>(url);
  }

  validatePartnerCode(code: string): Observable<boolean> {
    const url = `${this.usersUrl}/validate-institution-code`;
    return this.http.get<boolean>(url, { params: { code } });
  }
}
